import { DataSource } from '@angular/cdk/collections';
import { Component, Inject, ViewChild } from '@angular/core';
import { TechTableModule } from '@tech/components/tech-table';
import { TechIconModule } from '@tech/components/tech-icon';

import { TechSortDirective, TechSortModule } from '@tech/components/tech-sort';
import { TechButtonModule } from '@tech/components/tech-button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { TechLoaderModule } from '@tech/components/tech-loader';
import { CommonModule } from '@angular/common';
import { TechDividerModule } from '@tech/components/tech-divider';
import { TechFormFieldModule } from '@tech/components/tech-form-field';
import { TranslateModule } from '@ngx-translate/core';

export interface IFilters {
  label: string;
  type: string;
  id: string;
  value?: any;
}

export interface ICallbackResult<T> {
  count: Observable<number>;
  data: Observable<T[]>;
}

@Component({
  selector: 'app-permanent-table-paginated',
  standalone: true,
  imports: [
    TranslateModule,
    TechTableModule,
    TechIconModule,
    TechButtonModule,
    TechLoaderModule,
    TechDividerModule,
    TechFormFieldModule,
    TechSortModule,
    CommonModule,
  ],
  templateUrl: './permanent-table-paginated.component.html',
  styleUrl: './permanent-table-paginated.component.scss',
})
export class PermanentTablePaginatedComponent<T> {
  @ViewChild(TechSortDirective) sort!: TechSortDirective;
  dataSource: PaginatedDataSource<T>;
  displayColumns: string[] = [];
  loading$ = new BehaviorSubject<boolean>(true);
  callback: (
    page: number,
    size: number,
    selected_filters: IFilters[]
  ) => ICallbackResult<T>;
  available_filters: IFilters[] = [];
  selected_filters: IFilters[] = [];

  // own paginator
  current_page: number = 1;
  clickable_pages: number[] = [1, 2, 3, 4, 5];
  total_pages: number = 1;
  page_size: number = 25;
  total_query_count: number = 0;

  constructor(
    public dialogRef: MatDialogRef<PermanentTablePaginatedComponent<T>>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      receipts: DataSource<T>;
      display: string[];
      callback: (
        page: number,
        size: number,
        selected_filters: IFilters[]
      ) => ICallbackResult<T>;
      filters: IFilters[];
    }
  ) {
    this.dataSource = new PaginatedDataSource<T>();
    this.displayColumns = data.display;
    this.callback = data.callback;
    this.available_filters = data.filters;
    this.applyFilters();
  }
  numericButtonClick(page: number) {
    if (page > 3) {
      this.clickable_pages = [];
      this.clickable_pages = [page - 2, page - 1, page, page + 1, page + 2];
    } else if (page <= 3) {
      this.clickable_pages = [];
      this.clickable_pages = [1, 2, 3, 4, 5];
    }
    this.current_page = page;

    this.applyFilters();
  }

  changePageSize() {
    const selector = document.getElementById(
      'page-size-selector'
    ) as HTMLSelectElement;
    const val = Number.parseInt(selector.value, 10);
    this.page_size = val;
    this.current_page = 1;
    this.clickable_pages = [];
    this.clickable_pages = [1, 2, 3, 4, 5];
    this.applyFilters();
  }

  formatValue(value: unknown): string {
    if (value instanceof Date) {
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${pad(value.getDay())}/${pad(value.getMonth())}/${pad(value.getFullYear())} ${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
    }

    return String(value ?? '');
  }

  applyFilters() {
    this.selected_filters = [];
    this.available_filters.forEach((af) => {
      let input = document.getElementById(af.id) as HTMLInputElement;
      if (input?.type === 'date')
        this.selected_filters.push({ ...af, value: input.valueAsDate });
      else {
        this.selected_filters.push({ ...af, value: input?.value });
      }
    });
    let x = this.callback(
      this.current_page,
      this.page_size,
      this.selected_filters
    );
    x.count.subscribe({
      next: (d) => {
        this.total_pages = Math.ceil(d / this.page_size);
        this.total_query_count = d;
      },
    });
    x.data.subscribe({
      next: (d) => {
        if (d.length === 0) {
          this.dataSource.setData(d);
          this.loading$.next(true);
          setTimeout(() => {
            this.loading$.next(false);
          }, 1500);
        } else {
          this.dataSource.setData(d);
          this.loading$.next(false);
        }
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}

/*********************************************************
 *                                                       |
 * The templated DATASOURCE for the permanent receipts   |
 *                                                       |
 **********************************************************/
class PaginatedDataSource<T> extends DataSource<T> {
  private data = new BehaviorSubject<T[]>([]);

  connect(): Observable<T[]> {
    return this.data.asObservable();
  }

  /**
   * Adds a row to the datasource
   * @param row<T>
   */
  addRow(row: T) {
    const current = this.data.getValue();
    this.data.next([...current, row]);
  }

  /**
   * Sets the data of the datasource
   * @param rows<T[]>
   */
  setData(rows: T[]) {
    this.data.next(rows);
  }

  disconnect(): void {}
}
